import { OrgTypeSection } from "@/components/profile/OrgTypeSection";
import { usePermissao } from "@/lib/use-permissao";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { OrganizationType } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { getVisibleOrgTypeOptions } from "@/components/onboarding/constants";
import { OrganizationTypeGrid } from "@/components/organization/OrganizationTypeGrid";

// Essa seção só renderiza pra organização de equipe (a pessoal é tratada à parte, ver isPersonal
// abaixo) — DENTAL_OFFICE aparece como "Clínica Odontológica", não "Cirurgião Dentista".
const visibleTypeOptions = getVisibleOrgTypeOptions(false);

// ─── Seção "Área de atuação" (perfis Clínica/Laboratório) ──

export function AreaDeAtuacaoSecao() {
  const utils = trpc.useUtils();
  const meQuery = trpc.me.current.useQuery();
  const activeOrganization = meQuery.data?.activeOrganization;
  // A mesma régua do servidor (settings.manage) — dono e sócio sempre têm.
  const canManage = usePermissao("settings.manage");

  const [selected, setSelected] = useState<OrganizationType[]>(
    activeOrganization?.types ?? [],
  );
  const [dirty, setDirty] = useState(false);

  const currentTypes = activeOrganization?.types ?? [];
  if (!dirty && activeOrganization && selected.join(",") !== currentTypes.join(",")) {
    setSelected(currentTypes);
  }

  const updateTypes = trpc.organization.updateTypes.useMutation({
    onSuccess: () => {
      utils.me.current.invalidate();
      toast.success("Perfis da organização atualizados.");
      setDirty(false);
    },
    onError: (err) => toast.error(err.message || "Erro ao atualizar perfis."),
  });

  const toggle = (value: OrganizationType) => {
    setDirty(true);
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSave = () => {
    updateTypes.mutate({ types: selected });
  };

  // Organização pessoal escolhe uma área só (o mesmo seletor do onboarding).
  if (activeOrganization?.isPersonal) {
    return (
      <div className="px-6 py-5">
        <OrgTypeSection types={activeOrganization.types} />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-6 py-5">
      <div data-tour="config-organizacao-perfis" className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">
            Perfis da organização
          </h3>
          <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Define quais áreas aparecem no menu lateral. Uma organização pode
            combinar mais de um perfil — ou nenhum, mostrando tudo.
          </p>
        </div>

        <OrganizationTypeGrid
          options={visibleTypeOptions}
          selected={selected}
          onToggle={toggle}
          disabled={!canManage}
        />
      </div>

      {canManage && (
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateTypes.isPending || !dirty}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            {updateTypes.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Salvar
          </button>
        </div>
      )}
    </div>
  );
}
